import { Component, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { NZ_MODAL_DATA, NzModalRef } from 'ng-zorro-antd/modal';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzMessageService } from 'ng-zorro-antd/message';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { NpsService } from '@beautyapp/core';

@Component({
  selector: 'beautyapp-nps-survey',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    NzButtonModule,
    NzInputModule,
    TranslateModule,
  ],
  template: `
    <div class="nps-container">
      <p class="nps-question">{{ questionText }}</p>

      <div class="nps-scores">
        @for (score of scores; track score) {
        <button
          nz-button
          [nzType]="selectedScore === score ? 'primary' : 'default'"
          [class.detractor]="selectedScore === score && score <= 6"
          [class.passive]="selectedScore === score && score >= 7 && score <= 8"
          [class.promoter]="selectedScore === score && score >= 9"
          (click)="selectScore(score)"
        >
          {{ score }}
        </button>
        }
      </div>

      <div class="nps-comment" *ngIf="selectedScore !== null">
        <textarea
          nz-input
          [(ngModel)]="comment"
          [placeholder]="'nps.commentPlaceholder' | translate"
          [nzAutosize]="{ minRows: 4, maxRows: 4 }"
        >
        </textarea>
      </div>

      <div class="nps-submit" *ngIf="selectedScore !== null">
        <button
          nz-button
          nzType="primary"
          nzSize="large"
          [nzLoading]="submitting"
          (click)="submit()"
        >
          {{ 'nps.submit' | translate }}
        </button>
      </div>
    </div>
  `,
  styles: [
    `
      .nps-container {
        text-align: center;
        padding: 16px 0;
      }

      .nps-question {
        font-size: 16px;
        font-weight: 500;
        margin-bottom: 24px;
        color: #333;
      }

      .nps-scores {
        display: flex;
        gap: 4px;
        justify-content: center;
        flex-wrap: nowrap;
        margin-bottom: 20px;
      }

      .nps-scores button {
        min-width: 36px;
        width: 36px;
        height: 36px;
        padding: 0;
        font-size: 14px;
        font-weight: 600;
        border-radius: 8px;
      }

      .nps-scores button.detractor {
        background-color: #ff4d4f;
        border-color: #ff4d4f;
        color: #fff;
      }

      .nps-scores button.passive {
        background-color: #faad14;
        border-color: #faad14;
        color: #fff;
      }

      .nps-scores button.promoter {
        background-color: #52c41a;
        border-color: #52c41a;
        color: #fff;
      }

      .nps-labels {
        display: flex;
        justify-content: space-between;
        font-size: 12px;
        color: #999;
        margin-bottom: 20px;
        padding: 0 4px;
      }

      .nps-comment {
        margin-bottom: 16px;
      }

      .nps-submit {
        text-align: center;
      }
    `,
  ],
})
export class NpsSurveyComponent {
  private modal = inject(NzModalRef);
  private npsService = inject(NpsService);
  private message = inject(NzMessageService);
  private translate = inject(TranslateService);
  private modalData: any = inject(NZ_MODAL_DATA, { optional: true });

  scores = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10];
  selectedScore: number | null = null;
  questionText =
    this.modalData?.customQuestion || this.translate.instant('nps.question');
  comment = '';
  submitting = false;

  selectScore(score: number) {
    this.selectedScore = score;
  }

  submit() {
    if (this.selectedScore === null) return;

    this.submitting = true;
    this.npsService
      .submit({
        score: this.selectedScore,
        comment: this.comment || undefined,
      })
      .subscribe({
        next: () => {
          this.message.success(this.translate.instant('nps.thankYou'));
          this.modal.close(true);
        },
        error: () => {
          this.message.error(this.translate.instant('nps.error'));
          this.submitting = false;
        },
      });
  }
}
